<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { game } from '$stores/game.svelte';
  import { pair } from '$stores/pair.svelte';
  import { seal } from '$crypto/encryption';
  import Element from './Element.svelte';

  /** ============ Live tension state ============
   *  Local-only for V1: a 60s countdown timer + a simulated "friend is locking
   *  in" progress indicator. The relay-driven path lives in Wait.svelte after
   *  submit; once the friend submits incrementally these can be replaced by a
   *  real polling hook. */
  let secondsLeft = $state(60);
  let countTimer: ReturnType<typeof setInterval> | null = null;

  let friendProgress = $state(0); // 0..4 while user plays; jumps to 5 on user submit
  let friendTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleFriendTick() {
    const delayMs = 4_000 + Math.floor(Math.random() * 5_000);
    friendTimer = setTimeout(() => {
      if (friendProgress < 4) {
        friendProgress += 1;
        scheduleFriendTick();
      }
    }, delayMs);
  }

  onMount(() => {
    countTimer = setInterval(() => {
      if (secondsLeft > 0) secondsLeft -= 1;
    }, 1_000);
    scheduleFriendTick();
  });
  onDestroy(() => {
    if (countTimer) clearInterval(countTimer);
    if (friendTimer) clearTimeout(friendTimer);
  });

  /** Map an element id to its 0..4 position index in the current Pulse,
   *  so palette tiles + filled slots can use locked tile-pos-N colors. */
  function posOf(elId: string | null | undefined): number {
    if (!elId || !game.pulse) return -1;
    return game.pulse.elements.findIndex((e) => e.id === elId);
  }

  function clear() {
    game.clearAll();
  }

  /** Encrypt and post the user's ordering to the relay (paired flow only). */
  async function postOrderToRelay(order: string[]): Promise<boolean> {
    if (!pair.isBonded || !pair.pairKey || !game.pulse) return false;
    const payload = JSON.stringify({
      order,
      handle: game.userHandle,
      ts: Math.floor(Date.now() / 1000)
    });
    const sealed = seal(payload, pair.pairKey);
    try {
      const res = await fetch('/api/pulse/relay', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          pairId: pair.pairId,
          pulseId: game.pulse.id,
          sender: pair.myRole,
          ct: sealed.ct
        })
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async function submit() {
    if (!game.isReady) return;
    const u = game.userOrder.filter((x): x is string => x !== null);
    friendProgress = 5;

    if (pair.isBonded) {
      const ok = await postOrderToRelay(u);
      if (ok) {
        game.submitDeferred();
        return;
      }
    }
    game.submit();
  }

  /** Quest bonus pill: surface a small "+25 XP" hint when today's pulse id
   *  ends in a 7 (cheap, deterministic, recognisably "lucky"). */
  const bonusLabel = $derived.by(() => {
    if (!game.pulse) return '';
    return game.pulse.id.endsWith('7') ? '+25 XP BONUS' : '';
  });

  const friendName = $derived.by(() =>
    pair.isBonded ? pair.friendHandle : game.friendHandle || 'your twin'
  );
</script>

{#if game.pulse}
  <section class="play">
    <div class="play-header">
      <div class="timer-pill" class:urgent={secondsLeft < 10}>
        <span class="timer-dot"></span>
        <span>0:{String(Math.max(0, secondsLeft)).padStart(2, '0')} LEFT</span>
      </div>
    </div>

    <div class="quest-card">
      {#if bonusLabel}
        <span class="quest-bonus">{bonusLabel}</span>
      {/if}
      <span class="quest-tag">{game.pulse.categoryLabel} QUEST</span>
      <div class="quest-q">{game.pulse.prompt.text}</div>
      <div class="quest-sub">@{friendName} gets the same prompt. No peeking.</div>
    </div>

    <div class="palette-label">Tap an element, then tap a slot</div>
    <div class="palette">
      {#each game.pulse.elements as e, i (e.id)}
        <button
          class="shape-tile tile-pos-{i + 1}"
          class:selected={game.selected === e.id}
          class:placed={game.userOrder.includes(e.id)}
          style:animation-delay="{i * 60}ms"
          aria-label={e.name || e.label}
          onclick={() => game.selectShape(e.id)}
        >
          <Element el={e} sizePct={70} />
        </button>
      {/each}
    </div>

    <div class="rank-axis">
      <span>← {game.pulse.prompt.left}</span>
      <span>{game.pulse.prompt.right} →</span>
    </div>

    <div class="slot-rail">
      {#each Array(5) as _, i}
        {@const filledPos = posOf(game.userOrder[i])}
        <button
          class="slot tile-pos-{filledPos + 1}"
          class:filled={game.userOrder[i] !== null}
          onclick={() => game.tapSlot(i)}
        >
          <span class="slot-num">{i + 1}</span>
          {#if game.userOrder[i]}
            <Element el={game.pulse.elements.find((x) => x.id === game.userOrder[i])} sizePct={60} />
          {/if}
        </button>
      {/each}
    </div>

    <div class="friend-status">
      <div class="friend-typing">
        <span>@{friendName} is locking in</span>
        <span class="typing-dots"><span></span><span></span><span></span></span>
      </div>
      <div class="friend-progress">{Math.min(5, friendProgress)}/5</div>
    </div>

    <div class="action-row">
      <button class="btn btn-secondary clear-btn" onclick={clear} aria-label="Clear">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path
            d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14"
          />
        </svg>
      </button>
      <button class="btn btn-primary lock-btn" onclick={submit} disabled={!game.isReady}>
        Lock it in →
      </button>
    </div>
  </section>
{/if}

<style>
  .play {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px var(--gutter) max(var(--safe-cta-bottom), 12px);
    gap: 10px;
  }

  .play-header {
    display: flex;
    justify-content: center;
    margin-bottom: 2px;
  }
  .timer-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 0, 0, 0.6);
    border: 2px solid var(--gold);
    color: var(--gold);
    font-weight: 900;
    font-size: 11px;
    padding: 5px 14px;
    border-radius: var(--r-pill);
    box-shadow: var(--glow-gold);
    letter-spacing: 0.05em;
  }
  .timer-pill.urgent {
    border-color: var(--hot);
    color: var(--hot);
    animation: pulseUrgent 0.6s ease-in-out infinite;
  }
  .timer-dot {
    width: 6px;
    height: 6px;
    background: currentColor;
    border-radius: 50%;
    box-shadow: 0 0 8px currentColor;
  }
  @keyframes pulseUrgent {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  :global(body.brutal) .timer-pill {
    border-color: var(--text);
    color: var(--text);
    box-shadow: none;
    background: transparent;
  }

  /* ============ Quest card ============ */
  .quest-card {
    background: linear-gradient(135deg, rgba(167, 139, 250, 0.32), rgba(255, 56, 188, 0.22));
    border: 2px solid var(--violet);
    border-radius: var(--r-md);
    padding: 12px 14px;
    box-shadow: var(--depth-md) var(--violet-deep), 0 0 24px rgba(167, 139, 250, 0.25);
    position: relative;
    margin-bottom: 4px;
    animation: slideUp 400ms var(--spring-bouncy) backwards;
  }
  :global(body.brutal) .quest-card {
    background: var(--bg);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .quest-tag {
    display: inline-block;
    font-size: 10px;
    background: var(--violet);
    color: #2d1a5e;
    font-weight: 900;
    padding: 3px 10px;
    border-radius: var(--r-pill);
    letter-spacing: 0.1em;
    margin-bottom: 6px;
    text-transform: uppercase;
  }
  :global(body.brutal) .quest-tag {
    background: var(--text);
    color: var(--bg);
  }
  .quest-q {
    font-size: clamp(15px, 4.2vw, 18px);
    font-weight: 800;
    line-height: 1.25;
    color: var(--text);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
  }
  :global(body.brutal) .quest-q {
    text-shadow: none;
  }
  .quest-sub {
    font-size: 11px;
    color: var(--text-2);
    margin-top: 5px;
    font-weight: 600;
  }
  .quest-bonus {
    position: absolute;
    top: -8px;
    right: clamp(8px, 3vw, 12px);
    background: var(--gold);
    color: var(--text-on-light);
    font-weight: 900;
    font-size: 10px;
    padding: 4px 9px;
    border-radius: var(--r-pill);
    box-shadow: var(--depth-sm) var(--gold-deep);
    transform: rotate(4deg);
    transform-origin: center;
    letter-spacing: 0.05em;
  }

  /* ============ Palette tiles ============ */
  .palette-label {
    font-size: 10px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    text-align: center;
  }
  .palette {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: clamp(4px, 1.2vw, 8px);
    justify-content: center;
  }
  .shape-tile {
    aspect-ratio: 1;
    min-width: 0;
    max-width: 64px;
    width: 100%;
    justify-self: center;
    border-radius: var(--r-md);
    border: 2px solid rgba(255, 255, 255, 0.25);
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 900;
    color: #fff;
    letter-spacing: -0.01em;
    position: relative;
    overflow: hidden;
    transition: transform 120ms var(--spring-bouncy), box-shadow 120ms var(--spring-bouncy);
    cursor: pointer;
    animation: shapeIn 300ms var(--spring-bouncy) backwards;
  }
  /* Glossy highlight strip — applies to ALL tiles */
  .shape-tile::before {
    content: '';
    position: absolute;
    top: 8%;
    left: 8%;
    width: 35%;
    height: 28%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.45), transparent);
    border-radius: 8px;
    pointer-events: none;
  }
  /* Position-locked colors */
  .tile-pos-1 {
    background: linear-gradient(135deg, var(--tile-1), var(--tile-1-deep));
    box-shadow: var(--depth-md) var(--tile-1-shadow);
    --slot-shadow: var(--tile-1-shadow);
  }
  .tile-pos-2 {
    background: linear-gradient(135deg, var(--tile-2), var(--tile-2-deep));
    box-shadow: var(--depth-md) var(--tile-2-shadow);
    --slot-shadow: var(--tile-2-shadow);
  }
  .tile-pos-3 {
    background: linear-gradient(135deg, var(--tile-3), var(--tile-3-deep));
    box-shadow: var(--depth-md) var(--tile-3-shadow);
    --slot-shadow: var(--tile-3-shadow);
  }
  .tile-pos-4 {
    background: linear-gradient(135deg, var(--tile-4), var(--tile-4-deep));
    box-shadow: var(--depth-md) var(--tile-4-shadow);
    --slot-shadow: var(--tile-4-shadow);
  }
  .tile-pos-5 {
    background: linear-gradient(135deg, var(--tile-5), var(--tile-5-deep));
    box-shadow: var(--depth-md) var(--tile-5-shadow);
    --slot-shadow: var(--tile-5-shadow);
  }
  .shape-tile.selected {
    transform: translateY(-3px) scale(1.04);
    box-shadow: 0 7px 0 var(--slot-shadow, var(--tile-1-shadow)), 0 0 30px rgba(255, 216, 74, 0.5);
    border-color: var(--gold);
  }
  .shape-tile.placed {
    opacity: 0.3;
    transform: scale(0.94);
    pointer-events: none;
  }
  .shape-tile:active {
    transform: translateY(2px);
    box-shadow: 0 1px 0 var(--slot-shadow, var(--tile-1-shadow));
  }
  :global(body.brutal) .shape-tile {
    background: var(--bg);
    color: var(--text);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  :global(body.brutal) .shape-tile::before {
    display: none;
  }
  :global(body.brutal) .shape-tile.selected {
    background: var(--text);
    color: var(--bg);
    box-shadow: 4px 4px 0 0 var(--text);
  }

  .rank-axis {
    display: flex;
    justify-content: space-between;
    padding: 0 4px;
    font-size: 9px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
  }

  /* ============ Slot rail (the cage) ============ */
  .slot-rail {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: clamp(4px, 1.2vw, 6px);
    padding: 8px;
    background: rgba(0, 0, 0, 0.4);
    border: 2px solid var(--border-warm);
    border-radius: var(--r-md);
    box-shadow: inset 0 0 24px rgba(255, 56, 188, 0.12);
  }
  :global(body.brutal) .slot-rail {
    background: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .slot {
    aspect-ratio: 1;
    min-width: 0;
    background: transparent;
    border: 2px dashed rgba(255, 216, 74, 0.4);
    border-radius: var(--r-sm);
    display: grid;
    place-items: center;
    position: relative;
    transition: var(--transition);
    cursor: pointer;
  }
  :global(body.brutal) .slot {
    border: 2px dashed var(--text);
  }
  .slot:hover {
    border-color: var(--gold);
    background: rgba(255, 216, 74, 0.06);
  }
  .slot-num {
    position: absolute;
    top: 3px;
    left: 5px;
    font-size: 9px;
    font-weight: 900;
    color: var(--gold);
  }
  /* When a slot is filled it adopts the gradient + shadow of the placed
     element's tile-pos via the same .tile-pos-N class on the slot. */
  .slot.filled {
    border-style: solid;
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: var(--depth-sm) var(--slot-shadow, var(--tile-1-shadow));
  }
  .slot.filled .slot-num {
    color: rgba(255, 255, 255, 0.85);
  }
  :global(body.brutal) .slot.filled {
    background: var(--text);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  :global(body.brutal) .slot.filled .slot-num {
    color: var(--bg);
  }

  /* ============ Friend status ============ */
  .friend-status {
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid var(--border-warm);
    border-radius: var(--r-pill);
    padding: 6px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 10px;
  }
  :global(body.brutal) .friend-status {
    background: var(--bg);
    border: 2px solid var(--text);
  }
  .friend-typing {
    color: var(--hot);
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  :global(body.brutal) .friend-typing {
    color: var(--text);
  }
  .typing-dots {
    display: inline-flex;
    gap: 3px;
  }
  .typing-dots span {
    width: 4px;
    height: 4px;
    background: currentColor;
    border-radius: 50%;
    animation: typingDot 1.2s ease-in-out infinite;
  }
  .typing-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }
  .typing-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }
  @keyframes typingDot {
    0%, 80%, 100% {
      opacity: 0.3;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-2px);
    }
  }
  .friend-progress {
    color: var(--text);
    font-weight: 800;
  }

  /* ============ Action row ============ */
  .action-row {
    display: flex;
    gap: 8px;
    margin-top: auto;
  }
  .clear-btn {
    flex: 0 0 auto;
  }
  .lock-btn {
    flex: 1;
    text-align: center;
  }
  .clear-btn svg {
    width: 16px;
    height: 16px;
    display: block;
  }
  /* Destructive intent — paint the trash button red so its function reads
     instantly. Stacks on top of .btn / .btn-secondary so the structural
     rules (radius, padding, press translate, gloss strip) still apply. */
  .clear-btn {
    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
    color: #fff;
    border: 2px solid rgba(255, 255, 255, 0.35);
    box-shadow:
      var(--depth-md) #7f1d1d,
      0 0 18px rgba(239, 68, 68, 0.45);
  }
  .clear-btn:active {
    box-shadow:
      0 0 0 0 #7f1d1d,
      0 0 18px rgba(239, 68, 68, 0.45);
  }
  .clear-btn svg {
    stroke: #fff;
  }
  :global(body.brutal) .clear-btn {
    background: var(--text);
    color: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  :global(body.brutal) .clear-btn svg {
    stroke: var(--bg);
  }
</style>
